import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ArrowRight, Loader2, BookOpen } from "lucide-react";
import api from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function GuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/guides/${slug}`)
      .then((r) => setGuide(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: guide ? guide.title : "Guide pratique",
    description: guide ? guide.description : "Détail d'un guide pratique DemkaBiz",
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center" data-testid="guide-loading">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (error || !guide) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center" data-testid="guide-not-found">
        <h1 className="font-display font-bold text-3xl">Guide introuvable</h1>
        <Link to="/guide-pratique" className="btn-primary mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Retour aux guides
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="guide-detail-page">
      <Link to="/guide-pratique" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-6" data-testid="guide-back-link">
        <ArrowLeft className="w-4 h-4" /> Retour aux guides
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-10 items-start">
        {guide.cover_url && (
          <div className="rounded-3xl overflow-hidden shadow-lg bg-secondary" data-testid="guide-cover">
            <img src={guide.cover_url} alt={`Couverture — ${guide.title}`} className="w-full h-auto object-contain" />
          </div>
        )}

        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-primary text-xs font-medium mb-3">
            <BookOpen className="w-4 h-4" />
            Guide pratique
          </div>
          {guide.edition && (
            <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">{guide.edition}</p>
          )}
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-tight" data-testid="guide-title">
            {guide.title}
          </h1>
          {guide.subtitle && <p className="text-lg text-muted-foreground mt-3">{guide.subtitle}</p>}

          <p className="text-lg text-foreground/85 leading-relaxed mt-6" data-testid="guide-description">
            {guide.description}
          </p>

          {guide.long_description && (
            <div className="mt-8 space-y-4 text-foreground/80 leading-relaxed whitespace-pre-line" data-testid="guide-long-description">
              {guide.long_description}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            {guide.pdf_url ? (
              <a
                href={guide.pdf_url}
                download
                target="_blank"
                rel="noreferrer"
                className="btn-orange"
                data-testid="guide-download-btn"
              >
                <Download className="w-4 h-4" /> Télécharger le PDF
              </a>
            ) : (
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 font-medium" data-testid="guide-no-pdf">
                Le fichier PDF sera disponible sous peu.
              </div>
            )}
            <Link to="/inscription" className="btn-outline" data-testid="guide-register-cta">
              Créer un compte <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
